import { z } from 'zod';
import type {
  CustomFieldEntity,
  CustomFieldType,
  LeadSourceKind,
  ScoreCategory,
  StageKind,
  UserRole,
} from '../constants';
import type { Permission } from '../permissions';

export const LoginBody = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  password: z.string().min(1).max(200),
});

export const ChangePasswordBody = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(10, 'Use at least 10 characters').max(200),
});

export type CurrentUserDTO = {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  title: string | null;
  phone: string | null;
  timezone: string | null;
  emailSignature: string | null;
  permissions: Permission[];
};

/** Color tokens usable for stages, relationships, tiers, tags (mapped to classes by the web app). */
export const COLOR_TOKENS = [
  'slate',
  'gray',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'pink',
  'rose',
] as const;
export type ColorToken = (typeof COLOR_TOKENS)[number];

export type StageDTO = {
  id: string;
  key: string;
  label: string;
  color: string;
  sortOrder: number;
  kind: StageKind;
  isSystem: boolean;
  isActive: boolean;
};
export type RelationshipDTO = {
  id: string;
  key: string;
  label: string;
  color: string;
  sortOrder: number;
  blocksOutreach: boolean;
  isSystem: boolean;
  isActive: boolean;
};
export type TierDTO = { id: string; key: string; label: string; minScore: number; color: string; description: string | null };
export type UserSummaryDTO = { id: string; name: string; email: string; role: UserRole; isActive: boolean };
export type LeadSourceDTO = { id: string; key: string; name: string; kind: LeadSourceKind; isActive: boolean };
export type TagDTO = { id: string; name: string; color: string };
export type ProductCategoryDTO = { id: string; key: string; label: string; sortOrder: number; isActive: boolean };
export type ProductDTO = {
  id: string;
  name: string;
  sku: string | null;
  categoryKey: string;
  listPrice: number | null;
  currency: string;
  isActive: boolean;
};
export type TerritoryDTO = { id: string; name: string };
export type CustomFieldDefinitionDTO = {
  id: string;
  entity: CustomFieldEntity;
  key: string;
  label: string;
  type: CustomFieldType;
  options: string[];
  isRequired: boolean;
  isActive: boolean;
  sortOrder: number;
};
export type TemplateVariableDTO = { key: string; label: string; example: string };
export type ScoringRuleDTO = {
  id: string;
  key: string;
  label: string;
  description: string | null;
  category: ScoreCategory;
  kind: 'attribute' | 'signal' | 'tag';
  points: number;
  isEnabled: boolean;
  decayDays: number | null;
  maxOccurrences: number;
  params: Record<string, unknown>;
  isSystem: boolean;
  sortOrder: number;
};

/**
 * GET /api/meta — lookups the web app caches (stale time ~1 min) to render pickers, badges and labels.
 * Signal rules are included so "Record signal" menus list the configured signal types.
 */
export type MetaResponse = {
  stages: StageDTO[];
  relationships: RelationshipDTO[];
  tiers: TierDTO[];
  users: UserSummaryDTO[];
  sources: LeadSourceDTO[];
  tags: TagDTO[];
  productCategories: ProductCategoryDTO[];
  products: ProductDTO[];
  territories: TerritoryDTO[];
  customFields: CustomFieldDefinitionDTO[];
  signalRules: ScoringRuleDTO[];
  templateVariables: TemplateVariableDTO[];
  settings: {
    companyName: string;
    defaultCurrency: string;
    defaultTimezone: string;
    defaultCountry: string;
    locale: string;
    complianceMode: 'none' | 'casl' | 'can_spam';
    bulkConfirmThreshold: number;
    lostReasons: string[];
    stageProbabilities: Record<string, number>;
    automationEnabled: boolean;
  };
  integrations: {
    emailProvider: string;
    emailConfigured: boolean;
    discoveryProviders: { key: string; label: string; configured: boolean }[];
    aiConfigured: boolean;
  };
};
